import ActionsDropdown, { Action } from '@anm/components/ActionsDropdown';
import EditableField from '@anm/components/EditableField';
import { useDropdownDirection } from '@anm/components/PlayerBlock/hooks';
import Image from '@anm/components/image/Image';
import { DEFAULT_DROPDOWN_DELAY } from '@anm/constants/delays';
import { AnmLinkProps, LinkContext } from '@anm/contexts/LinkContext';
import getPassedTime from '@anm/helpers/getPassedTime';
import noop from '@anm/helpers/noop';
import useHover from '@anm/hooks/useHover';
import { useContext, FC } from 'react';

import {
  FolderBottom,
  FolderDescription,
  FolderDescriptionAndActionsWrapper,
  FolderPreview,
  FolderPreviewWrapper,
  FolderTitleBlock,
  FolderWrapper
} from './Wrapper';

export type TitleVariant = 'black' | 'gray';
export type FolderBlockCommonProps = {
  title: string;
  modified: number;
  preview?: string;
  titleVariant?: TitleVariant;
  onRename?: (value: string) => void;
};

export type FolderBlockProps = FolderBlockCommonProps & {
  linkProps: AnmLinkProps;
  isInputFocused: boolean;
  isPending?: boolean;
  canEdit?: boolean;
  onFieldFocused: (v: boolean) => void;
  actions: Action[];
};

const FolderBlock: FC<FolderBlockProps> = ({
  title,
  actions,
  isPending,
  preview,
  modified,
  linkProps,
  titleVariant = 'black',
  isInputFocused,
  onFieldFocused,
  canEdit = true,
  onRename = noop
}) => {
  const [hoverRef] = useHover<HTMLDivElement>();
  const [dropdownWrapperRef, dropdownDirection] = useDropdownDirection(actions.length);
  const { Link } = useContext(LinkContext);

  return (
    <FolderWrapper ref={hoverRef}>
      <FolderPreviewWrapper id="folder-preview" isPending={isPending}>
        <Link {...linkProps}>
          <FolderPreview data-attribute-foldername={title}>
            {preview && <Image src={preview} alt={title} draggable={false} />}
          </FolderPreview>
        </Link>
      </FolderPreviewWrapper>

      <FolderBottom>
        <FolderTitleBlock variant={titleVariant}>
          <EditableField
            hoverVariant="pencil"
            variant="cropMultiline"
            placeholder="Add title"
            onChange={onRename}
            initialValue={title}
            isActive={canEdit && isInputFocused}
            isFocusedOnClick={canEdit}
            onFieldFocused={onFieldFocused}
          />
        </FolderTitleBlock>
        <FolderDescriptionAndActionsWrapper>
          <FolderDescription>{getPassedTime(modified)}</FolderDescription>
          <div ref={dropdownWrapperRef} data-dropdown="folder dropdown">
            {canEdit && (
              <ActionsDropdown
                {...{ actions }}
                direction={dropdownDirection}
                openDelay={DEFAULT_DROPDOWN_DELAY}
                closeDelay={DEFAULT_DROPDOWN_DELAY}
              />
            )}
          </div>
        </FolderDescriptionAndActionsWrapper>
      </FolderBottom>
    </FolderWrapper>
  );
};

export default FolderBlock;
